import * as React from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { NetworkStatus } from '@apollo/client'
import {
  ContractVersionPageContractVersionFragment,
  ContractVersionsPageContractVersionRequestFragment,
  ContractVersionsPageDocument,
  ContractVersionsPageEmployeeActivityFragment,
  ContractVersionsPageEmployeeFragment,
  ContractVersionsPageIncidenceRuleFragment,
  ContractVersionsPageQuery,
  WorkingConditionVersionApplicationFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'

import BlankSlate from 'modules/contracts/components/BlankSlate'
import ContractDetails from 'modules/contracts/components/ContractDetails'
import ApprovalBanner from 'modules/contracts/components/ContractDetails/Header/ApprovalBanner'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import LoadingState from './LoadingState'

interface Props {
  employee: ContractVersionsPageEmployeeFragment
  features: ContractVersionsPageQuery['features']['featuresConnection']['nodes']
  contractVersionRequest: ContractVersionsPageContractVersionRequestFragment | undefined
  contractVersion: ContractVersionPageContractVersionFragment | undefined
  employeeContractVersions: ContractVersionPageContractVersionFragment[] | undefined
  workingConditionVersionApplication?: WorkingConditionVersionApplicationFragment
  employeeActivity?: ContractVersionsPageEmployeeActivityFragment | undefined
  incidenceRules: ContractVersionsPageIncidenceRuleFragment[]
  isAutoApprover: boolean
  hasTimeBalance: boolean
  hasTimeTrackingAnnualisationDailyValue: boolean
  hasFixedDiscontinuousContracts?: boolean
  hasLegacyJobCatalog?: boolean
  canSeeAdditionalCompensations?: boolean
  canSeeContractExtraInfo?: boolean
  canPromote?: boolean
  canEditContracts?: boolean
}

const ContractVersionsContent = ({
  employee,
  isAutoApprover,
  features,
  contractVersionRequest,
  contractVersion,
  employeeContractVersions,
  workingConditionVersionApplication,
  employeeActivity,
  incidenceRules,
  hasTimeBalance,
  hasTimeTrackingAnnualisationDailyValue,
  hasFixedDiscontinuousContracts,
  hasLegacyJobCatalog,
  canSeeContractExtraInfo,
  canPromote,
  canEditContracts,
}: Props) => {
  const [selectedContractVersion, setSelectedContractVersion] = React.useState<
    ContractVersionPageContractVersionFragment | undefined
  >()

  useUnsafeEffect(() => {
    setSelectedContractVersion(contractVersion)
  }, [contractVersion])

  const handleOnSelectContractVersion = (contractVersionId: number | undefined) => {
    const version = contractVersionId
      ? employeeContractVersions?.find((cv) => cv.id === contractVersionId)
      : employeeContractVersions?.find((cv) => cv.isReference)

    setSelectedContractVersion(version)
  }

  const showApprovalBanner =
    canPromote && !!contractVersionRequest && contractVersionRequest.status === 'pending'

  if (!employeeContractVersions || employeeContractVersions.length === 0 || !contractVersion)
    return (
      <>
        {showApprovalBanner && (
          <ApprovalBanner
            contractVersionRequest={contractVersionRequest}
            employeeName={employee.fullName}
          />
        )}
        <BlankSlate employee={employee} canEditContracts={canEditContracts} />
      </>
    )

  return (
    <ContractDetails
      employee={employee}
      features={features}
      selectedContractVersion={selectedContractVersion || contractVersion}
      setSelectedContractVersion={handleOnSelectContractVersion}
      employeeContractVersions={employeeContractVersions}
      contractVersionRequest={contractVersionRequest}
      workingConditionVersionApplication={workingConditionVersionApplication}
      employeeActivity={employeeActivity}
      incidenceRules={incidenceRules}
      isAutoApprover={isAutoApprover}
      hasTimeBalance={hasTimeBalance}
      hasTimeTrackingAnnualisationDailyValue={hasTimeTrackingAnnualisationDailyValue}
      hasFixedDiscontinuousContracts={hasFixedDiscontinuousContracts}
      hasLegacyJobCatalog={hasLegacyJobCatalog}
      canSeeContractExtraInfo={canSeeContractExtraInfo}
    />
  )
}

const ContractVersionsPage = () => {
  const { company, access, employee: sessionEmployee } = useCurrentSession()
  const { id: employeeIdParam, contractVersionId: contractVersionIdParam } = useParams()
  const employeeId = employeeIdParam ? Number(employeeIdParam) : sessionEmployee.id
  const contractVersionId = contractVersionIdParam ? Number(contractVersionIdParam) : null
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const {
    data,
    loading: loadingData,
    networkStatus,
  } = useQuery(ContractVersionsPageDocument, {
    variables: {
      accessId: access.id,
      companyId: company.id,
      employeeId,
    },
    notifyOnNetworkStatusChange: true,
  })

  // Combine loading state with refetch status to handle refetch scenarios
  const loading = loadingData || networkStatus === NetworkStatus.refetch

  const rawEmployeeContractVersions = data?.contracts.contractVersionsConnection.nodes ?? []
  const employeeContractVersions = rawEmployeeContractVersions.slice().sort((a, b) => {
    return new Date(a.effectiveOn).getTime() - new Date(b.effectiveOn).getTime()
  })
  const contractVersion = rawEmployeeContractVersions.find((version) =>
    contractVersionId ? version.id === contractVersionId : version.isReference
  )

  const features = data?.features?.featuresConnection?.nodes ?? []
  const hasFixedDiscontinuousContracts =
    features.find((feature) => feature.name === 'dev_fixed_discontinuous_contracts')?.enabled ??
    false
  const hasLegacyJobCatalog =
    features.find((feature) => feature.name === 'legacy_job_catalog')?.enabled ?? false

  const canSeeContract = data?.employees.employee?._policies?.seeContract.can ?? false
  const workingConditionVersionApplication = contractVersion?.versionApplication ?? undefined
  const canSeeContractExtraInfo = contractVersion?._policies?.seeContractExtraInfo?.can ?? false
  const contractVersionRequest = data?.contracts?.contractVersionRequestsConnection?.nodes[0]
  const employeeActivity = data?.contracts?.employeeActivitiesConnection?.nodes[0]

  const canPromote = data?.contracts?._policies?.contractPromote?.can ?? false
  const canEditContracts = data?.contracts?._policies?.contractEditContractVersion?.can ?? false
  const employee = data?.employees.employee
  const isAutoApprover =
    data?.approvals?.approvalGroupMembershipsConnection?.nodes?.some(
      (membership) => membership?.access?.id === access.id
    ) ?? false
  const hasTimeTrackingAnnualisationDailyValue =
    data?.companies?.featureAvailabilitiesConnection.nodes?.some(
      (feature) => feature.id === 'time_tracking_annualisation_daily_value' && feature.available
    ) ?? false
  const hasTimeBalance =
    data?.companies?.featureAvailabilitiesConnection.nodes?.some(
      (feature) => feature.id === 'time_balance' && feature.available
    ) ?? false

  const hiringIncidences = data?.bookkeepersManagement?.incidencesConnection?.nodes ?? []
  const hasHiringIncidence = hiringIncidences.length > 0
  const incidenceRules = hasHiringIncidence
    ? data?.bookkeepersManagement?.incidenceRulesConnection?.nodes
    : []

  useUnsafeEffect(() => {
    if (!loading && !canSeeContract) {
      navigate(navigation.employees.id.fullPath({ id: employeeId }))
    }
  }, [navigate, loading, navigation, employeeId, canSeeContract])

  if (loading) return <LoadingState />

  return (
    <ResponsivePage.Body
      authorized={canSeeContract}
      error={!employee || (!contractVersion && employeeContractVersions.length > 0)}
      loading={loading}
      variant="full"
    >
      {employee && (
        <ContractVersionsContent
          employee={employee}
          features={features}
          contractVersion={contractVersion}
          employeeContractVersions={employeeContractVersions}
          contractVersionRequest={contractVersionRequest}
          workingConditionVersionApplication={workingConditionVersionApplication}
          employeeActivity={employeeActivity}
          incidenceRules={incidenceRules || []}
          isAutoApprover={isAutoApprover}
          hasTimeBalance={hasTimeBalance}
          hasTimeTrackingAnnualisationDailyValue={hasTimeTrackingAnnualisationDailyValue}
          hasFixedDiscontinuousContracts={hasFixedDiscontinuousContracts}
          hasLegacyJobCatalog={hasLegacyJobCatalog}
          canSeeContractExtraInfo={canSeeContractExtraInfo}
          canPromote={canPromote}
          canEditContracts={canEditContracts}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default ContractVersionsPage
